const RECT_W    = 20;   // px — width of each rectangle (square)
const RECT_H    = 20;   // px — full height of each rectangle
const RECT_GAP  = 6;    // px — gap between rectangles
const RECT_R    = 2;    // px — border-radius
const INSET     = 16;   // px — distance from right edge of #slider-heading
const RECT_THIN = 1;    // px — starting height of fg rect on entry
const DUR       = 1000; // ms — must match carousel animation duration
const EASE      = "cubic-bezier(0.22, 1, 0.36, 1)"; // ease-out-quint
const BG_COLOR  = "#000000"; // always-visible background rects
const FG_COLOR  = "#ffffff"; // progress rects (white, animated)

export function initSliderIndicator(): void {
  const heading = document.getElementById("slider-heading");
  const slider  = document.getElementById("projects-slider");
  if (!heading || !slider) return;

  if (slider.querySelectorAll(".w-slider-dot").length === 0) {
    const waitObserver = new MutationObserver(() => {
      if (slider.querySelectorAll(".w-slider-dot").length > 0) {
        waitObserver.disconnect();
        setup(heading, slider);
      }
    });
    waitObserver.observe(slider, { childList: true, subtree: true });
    return;
  }

  setup(heading, slider);
}

function setup(heading: HTMLElement, slider: HTMLElement): void {
  if (getComputedStyle(heading).position === "static") {
    heading.style.position = "relative";
  }

  // Wrapper positions both layers in the same spot
  const wrap = document.createElement("div");
  wrap.style.cssText = [
    "position:absolute",
    `right:${INSET}px`,
    "top:50%",
    "transform:translateY(-50%)",
    "pointer-events:none",
    "visibility:hidden",
  ].join(";");
  heading.appendChild(wrap);

  const colStyle = [
    "display:flex",
    "flex-direction:column",
    "align-items:center",
    "justify-content:center",
  ].join(";");

  // Background column — one rect per slide, static, always visible
  const bgCol = document.createElement("div");
  bgCol.style.cssText = colStyle + ";position:relative";
  wrap.appendChild(bgCol);

  // Foreground column — progress rects, layered on top
  const fgCol = document.createElement("div");
  fgCol.style.cssText = colStyle + ";position:absolute;top:0;left:0;right:0";
  wrap.appendChild(fgCol);

  const fgRects: HTMLElement[] = [];

  function makeRect(color: string, animate: boolean): HTMLElement {
    const el = document.createElement("div");
    el.style.cssText = [
      `width:${RECT_W}px`,
      `height:${animate ? RECT_THIN : RECT_H}px`,
      `margin-bottom:${animate ? 0 : RECT_GAP}px`,
      `border-radius:${RECT_R}px`,
      `background:${color}`,
      "flex-shrink:0",
      animate
        ? `transition:height ${DUR}ms ${EASE},margin-bottom ${DUR}ms ${EASE}`
        : "transition:none",
    ].join(";");
    return el;
  }

  // Build static background rects (one per slide)
  const dots = slider.querySelectorAll(".w-slider-dot");
  dots.forEach(() => {
    bgCol.appendChild(makeRect(BG_COLOR, false));
  });

  // Foreground helpers
  function addFg(): void {
    const el = makeRect(FG_COLOR, true);
    fgCol.appendChild(el);
    fgRects.push(el);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.height       = `${RECT_H}px`;
        el.style.marginBottom = `${RECT_GAP}px`;
      });
    });
  }

  function removeFg(): void {
    if (fgRects.length === 0) return;
    const el = fgRects.pop()!;
    el.style.height       = "0px";
    el.style.marginBottom = "0px";
    el.addEventListener("transitionend", () => el.remove(), { once: true });
  }

  function syncFg(): void {
    const allDots = slider.querySelectorAll(".w-slider-dot");
    let activeIndex = 0;
    allDots.forEach((dot, i) => {
      if (dot.classList.contains("w-active")) activeIndex = i;
    });

    const target = activeIndex; // slide 0 = 0 white rects; first click (index 1) = 1 rect
    const diff   = target - fgRects.length;
    if (diff > 0) {
      for (let i = 0; i < diff;  i++) addFg();
    } else if (diff < 0) {
      for (let i = 0; i < -diff; i++) removeFg();
    }
  }

  let hasInteracted = false;

  function onInteraction(): void {
    if (!hasInteracted) {
      hasInteracted = true;
      wrap.style.visibility = "visible";
    }
    syncFg();
  }

  const observer = new MutationObserver(onInteraction);
  slider.querySelectorAll(".w-slider-dot").forEach(dot => {
    observer.observe(dot, { attributes: true, attributeFilter: ["class"] });
  });
}
